import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import WishireLogo from "@/components/wishire/WishireLogo";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";

const SECTION_IDS = ["top", "for-colleges", "for-students", "wishire-score"];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  const { user } = useAuth() || {};

  // Scroll progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 200, damping: 30, restDelta: 0.001 });

  // Scrolled state for glass bg
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section tracking via IntersectionObserver
  useEffect(() => {
    const observers = [];
    const sectionMap = new Map();

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          sectionMap.set(id, entry.isIntersecting);
          // Pick the first intersecting section in DOM order
          const active = SECTION_IDS.find((s) => sectionMap.get(s));
          if (active) setActiveSection(active);
        },
        { threshold: 0.25, rootMargin: "-60px 0px -40% 0px" }
      );

      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const onLanding = typeof window !== "undefined" && window.location.pathname === "/";
  const links = onLanding ? [
    { id: "nav-colleges", label: "For Colleges", href: "#for-colleges", section: "for-colleges" },
    { id: NAV.candidates, label: "For Students", href: "#for-students", section: "for-students" },
    { id: NAV.score, label: "The Score", href: "#wishire-score", section: "wishire-score" },
    { id: "nav-guides", label: "Guides", href: "/guides", section: "guides" },
  ] : [
    { id: "nav-colleges", label: "For Colleges", href: "/for-colleges", section: "for-colleges" },
    { id: NAV.candidates, label: "For Students", href: "/for-students", section: "for-students" },
    { id: "nav-tests", label: "Company Tests", href: "/company-tests", section: "company-tests" },
    { id: "nav-guides", label: "Guides", href: "/guides", section: "guides" },
  ];

  const mobileMenuVariants = {
    hidden: { opacity: 0, y: -12, height: 0 },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
      opacity: 0,
      y: -8,
      height: 0,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
  };

  const linkItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.05, duration: 0.25, ease: "easeOut" },
    }),
    exit: { opacity: 0, x: -6, transition: { duration: 0.15 } },
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      {/* Scroll progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#C2410C] origin-left z-[60]"
        style={{ scaleX }}
      />

      <div className="container-x flex items-center justify-between py-4">
        <a href={onLanding ? "#top" : "/"} data-testid={NAV.logo} className="flex items-center">
          <WishireLogo size="lg" />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => {
            const isActive = activeSection === l.section;
            return (
              <a
                key={l.id}
                data-testid={l.id}
                href={l.href}
                className="relative text-sm transition-colors duration-200 group"
                style={{ color: isActive ? "#1C1917" : "" }}
              >
                <span
                  className={`transition-colors duration-200 ${
                    isActive
                      ? "text-stone-900 font-medium"
                      : "text-stone-700 hover:text-stone-900"
                  }`}
                >
                  {l.label}
                </span>
                {/* Underline indicator */}
                <span
                  className="absolute -bottom-1 left-0 h-px bg-[#C2410C] transition-all duration-300"
                  style={{ width: isActive ? "100%" : "0%" }}
                />
                {/* Hover underline for non-active links */}
                {!isActive && (
                  <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full bg-[#C2410C] transition-all duration-300" />
                )}
              </a>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        {user ? (
          <Link
            to={
              user.is_admin
                ? "/admin"
                : user.role === "company"
                ? "/company"
                : !user.role || user.onboarding_step === "role"
                ? "/onboarding/role"
                : "/app"
            }
            data-testid="nav-app-dashboard"
            className="hidden md:inline-flex btn-primary text-sm"
          >
            Open dashboard
          </Link>
        ) : (
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-signin"
              className="text-sm text-stone-700 hover:text-stone-900 font-medium transition-colors duration-200"
            >
              Sign in
            </Link>
            <a
              href="#book-demo"
              data-testid={NAV.waitlistCta}
              className="btn-primary text-sm"
            >
              Book a demo
            </a>
          </div>
        )}

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label="Toggle menu"
          data-testid={NAV.mobileToggle}
          className="md:hidden p-2 rounded-full border border-stone-300 bg-white/70 transition-colors hover:bg-white"
          onClick={() => setOpen((v) => !v)}
        >
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="close"
                initial={{ rotate: -45, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 45, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="block"
              >
                <X size={18} />
              </motion.span>
            ) : (
              <motion.span
                key="menu"
                initial={{ rotate: 45, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -45, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="block"
              >
                <Menu size={18} />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile menu — AnimatePresence slide-down */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="md:hidden glass border-t border-stone-200 overflow-hidden"
          >
            <div className="container-x py-5 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  custom={i}
                  variants={linkItemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={() => setOpen(false)}
                  className={`text-base py-2 px-3 rounded-xl transition-colors duration-150 ${
                    activeSection === l.section
                      ? "text-stone-900 font-medium bg-stone-100/60"
                      : "text-stone-700 hover:text-stone-900 hover:bg-stone-100/40"
                  }`}
                >
                  {activeSection === l.section && (
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C2410C] mr-2 mb-0.5" />
                  )}
                  {l.label}
                </motion.a>
              ))}

              <div className="mt-3 pt-3 border-t border-stone-200 flex flex-col gap-2">
                {user ? (
                  <motion.div
                    custom={links.length}
                    variants={linkItemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Link
                      to="/app"
                      onClick={() => setOpen(false)}
                      className="btn-primary justify-center w-full"
                    >
                      Open dashboard
                    </Link>
                  </motion.div>
                ) : (
                  <>
                    <motion.div
                      custom={links.length}
                      variants={linkItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <Link
                        to="/login"
                        onClick={() => setOpen(false)}
                        className="text-stone-800 text-base font-medium py-2 px-3 block"
                      >
                        Sign in
                      </Link>
                    </motion.div>
                    <motion.div
                      custom={links.length + 1}
                      variants={linkItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <a
                        href="#book-demo"
                        onClick={() => setOpen(false)}
                        className="btn-primary justify-center w-full"
                      >
                        Book a demo
                      </a>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
